import { useState } from "react";
import { Loader2, X, GripVertical, Scissors, Undo2, RotateCw, RotateCcw, Download } from "lucide-react";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { photoUrl } from "@/lib/api";
import { savePhotosToDevice } from "@/lib/savePhotos";

/**
 * Draggable, reorderable photo grid used by the Draft Creator.
 * Extracted from DraftCreator.jsx — same props, no behavior change.
 */
export default function PhotoGrid({
  photos,
  setPhotos,
  removePhoto,
  onRemoveBackground,
  onRevertBackground,
  onRotate,
  bgRemovingId,
  rotatingId,
  saveName,
}) {
  const [savingAll, setSavingAll] = useState(false);
  const saveAll = async () => {
    setSavingAll(true);
    try { await savePhotosToDevice(photos, saveName); } finally { setSavingAll(false); }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 350, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 350, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPhotos((items) => {
      const oldIndex = items.findIndex((p) => p.id === active.id);
      const newIndex = items.findIndex((p) => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          {photos.length} photo{photos.length !== 1 ? "s" : ""} · first is cover
        </p>
        <button type="button" onClick={saveAll} disabled={savingAll || !photos.length}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          data-testid="save-all-photos-btn">
          {savingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Save all to Photos
        </button>
      </div>
      <p className="mb-2 text-[10px] text-slate-400">Long-press to reorder · <Scissors className="inline h-2.5 w-2.5" /> cleans background · <Download className="inline h-2.5 w-2.5" /> saves to Photos</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-3 gap-2" data-testid="photo-grid" data-noswipe>
            {photos.map((p, i) => (
              <SortablePhoto
                key={p.id}
                photo={p}
                index={i}
                onRemove={removePhoto}
                onRemoveBackground={onRemoveBackground}
                onRevertBackground={onRevertBackground}
                onRotate={onRotate}
                isProcessing={bgRemovingId === p.id}
                isRotating={rotatingId === p.id}
                saveName={saveName}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SavePhotoButton({ photo, saveName }) {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" onPointerDown={(e) => e.stopPropagation()}
      onClick={async (e) => { e.stopPropagation(); setBusy(true); try { await savePhotosToDevice([photo], saveName); } finally { setBusy(false); } }}
      disabled={busy}
      className="rounded-full bg-sky-600/90 p-1 text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
      data-testid={`save-photo-${photo.id}`} aria-label="Save to Photos" title="Save to Photos">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
    </button>
  );
}

function SortablePhoto({ photo, index, onRemove, onRemoveBackground, onRevertBackground, onRotate, isProcessing, isRotating, saveName }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 20 : "auto",
    scale: isDragging ? "1.03" : "1",
  };
  const hasParent = !!photo.parent_id;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`photo-tile-${photo.id}`}
      aria-label="Long-press and drag to reorder"
      className={`photo-tile relative touch-none select-none overflow-hidden rounded-md border ${isDragging ? "border-[#0053DA] shadow-lg" : "border-slate-200"} bg-white cursor-grab active:cursor-grabbing`}
    >
      <img
        src={photoUrl(photo.id, "card", photo.rotated_at)}
        alt={photo.filename}
        className="pointer-events-none aspect-square w-full select-none object-cover"
        data-testid={`photo-thumb-${photo.id}`}
        draggable={false}
        loading="lazy"
      />
      {isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-white/80 backdrop-blur-sm">
          <Loader2 className="h-5 w-5 animate-spin text-[#0053DA]" />
          <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">Cleaning…</span>
        </div>
      )}
      {isRotating && !isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-white/70 backdrop-blur-sm">
          <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
          <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">Rotating…</span>
        </div>
      )}
      {index === 0 && (
        <span className="pointer-events-none absolute left-1 top-1 rounded-full bg-[#0053DA] px-1.5 py-0.5 text-[10px] font-semibold text-white">
          COVER
        </span>
      )}
      {hasParent && (
        <span className="pointer-events-none absolute left-1 bottom-7 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          CLEAN BG
        </span>
      )}
      <span className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-slate-900/70 p-1 text-white">
        <GripVertical className="h-3 w-3" />
      </span>
      {onRotate && (
        <div className="absolute bottom-1 right-1 flex gap-1">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRotate(photo.id, "ccw"); }}
            disabled={isRotating || isProcessing}
            className="rounded-full bg-slate-900/80 p-1 text-white transition-colors hover:bg-slate-900 disabled:opacity-50"
            data-testid={`rotate-ccw-${photo.id}`}
            aria-label="Rotate counter-clockwise"
            title="Rotate 90° left"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRotate(photo.id, "cw"); }}
            disabled={isRotating || isProcessing}
            className="rounded-full bg-slate-900/80 p-1 text-white transition-colors hover:bg-slate-900 disabled:opacity-50"
            data-testid={`rotate-cw-${photo.id}`}
            aria-label="Rotate clockwise"
            title="Rotate 90° right"
          >
            <RotateCw className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="absolute right-1 top-1 flex flex-col gap-1">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRemove(photo.id); }}
          className="rounded-full bg-slate-900/80 p-1 text-white transition-colors hover:bg-slate-900"
          data-testid={`remove-photo-${photo.id}`}
          aria-label="Remove photo"
        >
          <X className="h-3 w-3" />
        </button>
        {hasParent ? (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRevertBackground(photo); }}
            disabled={isProcessing}
            className="rounded-full bg-amber-500/90 p-1 text-white transition-colors hover:bg-amber-600"
            data-testid={`revert-bg-${photo.id}`}
            aria-label="Revert to original"
            title="Revert to original"
          >
            <Undo2 className="h-3 w-3" />
          </button>
        ) : (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemoveBackground(photo.id); }}
            disabled={isProcessing}
            className="rounded-full bg-emerald-600/90 p-1 text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
            data-testid={`remove-bg-${photo.id}`}
            aria-label="Remove background"
            title="Clean background to white"
          >
            <Scissors className="h-3 w-3" />
          </button>
        )}
        <SavePhotoButton photo={photo} saveName={saveName} />
      </div>
    </div>
  );
}
